import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import 'components/styles/main-form.css';
import CustomInput from 'components/custom-input';
import SideChart from 'components/side-chart';
import { isLoyaltyCardNumberValid, isCheckBoxChecked, isEmailIdValid } from 'form-validations';
import { useForm } from 'hooks/useForm';
import axios from 'axios';
import { OTP_ACTIONS, PARTNERSHIP_URL } from '../constants/index';
import SuccessfullyLinked from './successfully-linked';
import OtpScreen from './otp-screen';
import { ReactComponent as HintIcon } from 'assets/images/hint-icon.svg';
import ResponsiveImage from 'components/responsive-image';

const MainForm = ({ partner = {} }) => {
  const { t } = useTranslation();
  const { images = {}, config = {}, countryOfOperation } = partner;
  const { auth = {} } = config;
  const { email = {}, enabled } = auth;
  const { modes = [] } = email;

  const renderOfferEligibilityImage = () => {
    if (!images.offer) return null;

    return (
      <div className="offer-eligibility-container">
        <ResponsiveImage
          images={images.offer}
          alt="Offer Eligibility"
          className="offer-eligibility-image"
        />
      </div>
    );
  };

  // Map link loyalty card API error codes to translation keys
  const getTranslatedError = (errorCode, errorMessage) => {
    const errorMap = {
      EmployeeAlreadyLinked: 'PARTNERSHIP_PORTAL.EMPLOYEE_ALREADY_LINKED',
      InvalidCardNumberError: 'PARTNERSHIP_PORTAL.INVALID_LOYALTY_CARD'
    };
    const translationKey = errorMap[errorCode];
    return translationKey ? t(translationKey) : errorMessage || errorCode;
  };

  const hasKeys = (obj) =>Object.keys(obj).length;

  const showEmailIdField = enabled && hasKeys(email) && modes.length > 0 && modes[0];

  const {
    cardNumber,
    setCardNumber,
    emailId,
    setEmailId,
    companyCode,
    setCompanyCode,
    checkboxChecked,
    setCheckboxChecked,
    cardNumberError,
    setCardNumberError,
    emailIdError,
    setEmailIdError,
    checkboxError,
    setCheckboxError,
    isFormValid,
    data
  } = useForm(showEmailIdField);

  const [linkErrorCode, setLinkErrorCode] = useState('');
  const [linkErrorMessage, setLinkErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [showOtp, setShowOtp] = useState(false);
  const [otpUuid, setOtpUuid] = useState('');

  const handleCardNumberChange = (value) => {
    setCardNumber(value);
    setLinkErrorCode('');
    setLinkErrorMessage('');
  };

  const handleEmailIdChange = (value) => {
    setEmailId(value);
    setLinkErrorCode('');
    setLinkErrorMessage('');
  };

  const handleSubmit = async () => {
    const isValidLoyaltyCardNumber = isLoyaltyCardNumberValid(cardNumber, setCardNumberError, true);
    const isValidEmailId = showEmailIdField ? isEmailIdValid(emailId, setEmailIdError, true) : true;
    const isValidCheckBox = isCheckBoxChecked(checkboxChecked, setCheckboxError, true);

    if (isValidLoyaltyCardNumber && isValidEmailId && isValidCheckBox) {
      setIsSubmitting(true);
      let payload = {
        partnerId: companyCode,
        cardNumber: cardNumber
      };
      if (showEmailIdField) {
        payload = {
          ...payload,
          email: emailId,
          action: OTP_ACTIONS.PARTNERSHIP_EMAIL_VERIFICATION,
          mode: modes[0]
        };
      }
      try {
        const response = await axios.post(`${PARTNERSHIP_URL}/employees/link-employee`, payload, {
          headers: { 'Content-Type': 'application/json' }
        });
        setLinkErrorCode('');
        setLinkErrorMessage('');
        setIsSubmitting(false);
        if (!showEmailIdField) {
          setIsFormSubmitted(true);
          setCardNumber('');
          setCheckboxChecked(false);
        } else {
          setOtpUuid(response?.data?.data?.uuid || '');
          setShowOtp(true);
        }
      } catch (error) {
        setLinkErrorCode(error.response?.data?.code);
        setLinkErrorMessage(error.response?.data?.message);
        setIsSubmitting(false);
      }
    }
  };


  const [searchParams] = useSearchParams();

  useEffect(() => {
    let timer;
    if (isFormSubmitted) {
      timer = setTimeout(() => {
        setIsFormSubmitted(false);
      }, 4000);
    }
    return () => clearTimeout(timer);
  }, [isFormSubmitted]);

  useEffect(() => {
    const partnerId = searchParams.get('partnerId');
    if (partnerId) {
      setCompanyCode(partnerId);
    }
  }, [searchParams, setCompanyCode]);

  const isDisabled = !isFormValid || isSubmitting;

  const handleOtpSuccess = () => {
    setShowOtp(false);
    setIsFormSubmitted(true);
    setOtpUuid('');
    setEmailId('');
    setCardNumber('');
    setCheckboxChecked(false);
  };

  return (
    <div className="container">
      <div>
        {showOtp ? (
          <OtpScreen
            otpUuid={otpUuid}
            setOtpUuid={setOtpUuid}
            partnerId={companyCode}
            email={emailId}
            mode={modes[0]}
            cardNumber={cardNumber}
            onSuccess={handleOtpSuccess}
            onBack={() => setShowOtp(false)}
            onChangeEmail={() => setShowOtp(false)}
            countryOfOperation={countryOfOperation}
          />
        ) : (
          <div className="linkAccount">
            <div className="header">{t('PARTNERSHIP_PORTAL.LINK_YOUR_ACCOUNT')}</div>
            <div className="form">
              <div className="bottomRow">
                {showEmailIdField && (
                  <CustomInput
                    type="text"
                    label={t('PARTNERSHIP_PORTAL.EMAIL_ID')}
                    value={emailId}
                    error={emailIdError ? t('PARTNERSHIP_PORTAL.ENTER_VALID_EMAIL_ID') : ''}
                    onChange={handleEmailIdChange}
                    onBlur={() => isEmailIdValid(emailId, setEmailIdError, true)}
                    fullLength={true}
                  />
                )}
                <CustomInput
                  type="text"
                  label={t('PARTNERSHIP_PORTAL.LOYALTY_CARD_NUMBER')}
                  value={cardNumber}
                  error={cardNumberError ? t('PARTNERSHIP_PORTAL.ENTER_VALID_LOYALTY_CARD') : ''}
                  onChange={handleCardNumberChange}
                  onBlur={() => isLoyaltyCardNumberValid(cardNumber, setCardNumberError, true)}
                  fullLength={true}
                />
              </div>
              {linkErrorCode && (
                <div className="checkBoxError">
                  <HintIcon />
                  {getTranslatedError(linkErrorCode, linkErrorMessage)}
                </div>
              )}
              <hr />
              <div className="termsAnsConditions">
                <div>
                  <div className="align">
                    <input
                      type="checkbox"
                      className="checkbox"
                      checked={checkboxChecked}
                      onChange={(e) => {
                        setCheckboxChecked(!checkboxChecked);
                      }}
                    />
                    <span>{t('PARTNERSHIP_PORTAL.CONFIRM_LOYALTY_ID')}</span>
                  </div>
                  <SideChart data={data} />
                </div>
                {checkboxError && (
                  <div className="checkBoxError">
                    <HintIcon />
                    {checkboxError}
                  </div>
                )}
              </div>
              <button
                onClick={handleSubmit}
                disabled={isDisabled}
                className={`submit ${isDisabled ? 'inactive' : ''}`}>
                {t('COMMON.SUBMIT')}
              </button>
            </div>
          </div>
        )}
        {isFormSubmitted && <SuccessfullyLinked />}
        {renderOfferEligibilityImage()}
      </div>
    </div>
  );
};

MainForm.propTypes = {
  partner: PropTypes.object
};

export default MainForm;
